import safeMatch from './safe-match';
import {
  APP_MANIFEST_URL_TEMPLATE,
  CDN_BASE_URL,
  COMPANY_BASE_URL,
  SUPPORT_EMAIL,
} from '../constants';
import { sessionStorage } from './namespaced-storage';
import { window } from '../env/browser';

export const toQueryString = (
  query: Record<string, string | number | boolean> = {}
): string =>
  Object.keys(query).reduce(
    (result, key, i) =>
      `${result}${i > 0 ? '&' : '?'}${key}=${encodeURIComponent(query[key])}`,
    ''
  );

export const extractHash = (url: string): string =>
  ((hash) => (hash && `#${hash}`) || '')(url.split('#')[1] || '');

export const extractPathAndQuery = safeMatch(2)(
  /^(https?:\/{2,}[^/?#]*)?(.*?)(#.*)?$/
);

export const getAppManifestUrl = (appName: string): string =>
  sessionStorage.getItem(`APP_MANIFEST_URL:${appName}`) ||
  APP_MANIFEST_URL_TEMPLATE.replace('__APPNAME__', `${appName}-app`);

export const getCoreUrl = (
  path = '',
  query: Record<string, string | number | boolean> = {}
): string => `${COMPANY_BASE_URL}/merchant/${path}${toQueryString(query)}`;

export const getLoginUrl = (redirectTo?: string): string => {
  const baseUrl = getCoreUrl('sign_in');
  return redirectTo
    ? `${baseUrl}${toQueryString({ redirect_to: redirectTo })}`
    : baseUrl;
};

export const getLogoutUrl = (): string => getCoreUrl('sign_out');

export const getSupportContactUrl = (): string => `mailto:${SUPPORT_EMAIL}`;

export const getLogoUrl = (companyName: string): string =>
  `${CDN_BASE_URL}/images/logo-${companyName}.png`;

export const removeQueryParams = (url?: string): string =>
  (url && url.split('?')[0]) || '';

export const extractPathAndHash = (url: string): string =>
  removeQueryParams(extractPathAndQuery(url)) +
  removeQueryParams(extractHash(url));

export const getDocumentUrl = (withMutableResponse = false): string => {
  const baseUrl = withMutableResponse
    ? CDN_BASE_URL.replace(/\d+$/, 'current')
    : CDN_BASE_URL;
  return `${baseUrl}/index.html`;
};

interface SetQueryStringParameterOptions {
  name: string;
  value: string;
  append?: boolean;
  cb?: () => void;
}

export const setQueryStringParameter = ({
  name,
  value,
  append = false,
  cb,
}: SetQueryStringParameterOptions): void => {
  const url = new URL(window.document.URL);
  url.searchParams.delete(name);
  if (append) url.searchParams.append(name, value);
  else url.searchParams.set(name, value);
  window.history.replaceState(null, '', url.toString());

  if (typeof cb === 'function') {
    setTimeout(cb, 500);
  }
};

const LEGACY_CALENDAR_PATH = /^((?:https?:\/\/[^/?#]+)?)\/calendar(?=[/?#]|$)/;
const LEGACY_APPOINTMENT_HASH = /#\/appointment(?=[/?]|$)/;

// The old calendar opens `?appointment_id=<id>` itself; calendar-v2 leaves
// appointments to the sidebar, so the id moves into the hash.
const appointmentIdToHash = (url: string): string => {
  if (url.includes('#')) return url;

  const [path, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  const appointmentId = params.get('appointment_id');
  if (!appointmentId) return url;

  params.delete('appointment_id');
  const rest = params.toString();
  return `${path}${rest ? `?${rest}` : ''}#/appointment/${appointmentId}`;
};

export const toGravityCalendarUrl = (url: string): string => {
  const calendarUrl = LEGACY_CALENDAR_PATH.test(url)
    ? appointmentIdToHash(url).replace(LEGACY_CALENDAR_PATH, '$1/calendar-v2')
    : url;

  return calendarUrl.replace(LEGACY_APPOINTMENT_HASH, '#/appointment-v4');
};
